import { useAppSelector } from 'universal-scripts'
import { useDashboard } from '@hooks/useDashboard'
import { Link } from 'react-router'
import DashboardGridSkeleton from '@components/skeletons/DashboardGridSkeleton/DashboardGridSkeleton'
import Popover from '@components/Popover/Popover'
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl'
import AvatarPlaceholder from '@components/AvatarPlaceholder/AvatarPlaceholder'
import ShareButton from '@components/ShareButton/ShareButton'
import DashboardGrid from './DashboardGrid/DashboardGrid'
import PrivacyTag from '@components/PrivacyTag/PrivacyTag'
import PrintButton from '@components/PrintButton/PrintButton'

import './DashboardContent.css'

interface DashboardContentProps {
  id: string
  isEmbed?: boolean
}

export default function DashboardContent({
  id,
  isEmbed = false
}: DashboardContentProps) {
  const intl = useIntl()
  const dashboardData = useDashboard(id)
  const isLoggedIn = useAppSelector((state) => !!state.user)

  if (!dashboardData || !__CLIENT__) return <DashboardGridSkeleton />

  const dashboardPath = `/dashboards/${dashboardData.id}`
  const embedPath = `/embed/dashboards/${dashboardData.id}`
  const shareUrl = `${window.location.origin}${embedPath}`

  return (
    <section id="dashboard-content" className="main-content">
      <header id="dashboard-header">
        <div className="side left-side">
          <div className="dashboard-header-group dashboard-header-group-1">
            <h1 title={dashboardData.title} className="ellipsis">
              {dashboardData.title}
            </h1>
            <Popover
              popoverClassName="popover-info no-print"
              buttonClassName="info-btn no-print"
              triggerContent={<span className="icon icon-info" />}
            >
              <FormattedMessage
                id="dashboard-item.created"
                values={{
                  time: (
                    <FormattedDate
                      key="time"
                      value={dashboardData.created}
                      year="numeric"
                      month="short"
                      day="numeric"
                    />
                  )
                }}
              />
            </Popover>
          </div>
          <div className="dashboard-header-group">
            <div className="user">
              <AvatarPlaceholder text={dashboardData.owner.name} />
              <span>{dashboardData.owner.name}</span>
            </div>
            {!isEmbed && (
              <PrivacyTag
                privacy={dashboardData.public ? 'public' : 'private'}
              />
            )}
          </div>
        </div>
        <div className="side right-side no-print">
          <ShareButton
            text={dashboardData.description}
            title={dashboardData.title}
            url={shareUrl}
          />
          {isEmbed ? (
            isLoggedIn ? (
              <Link
                title={intl.formatMessage({ id: 'dashboard.link.full' })}
                className="button icon icon-minimize"
                to={dashboardPath}
                aria-label={intl.formatMessage({ id: 'dashboard.link.full' })}
              />
            ) : null
          ) : (
            <Link
              title={intl.formatMessage({ id: 'dashboard.link.embed' })}
              className="button icon icon-maximize"
              to={embedPath}
              aria-label={intl.formatMessage({ id: 'dashboard.link.embed' })}
            />
          )}
          {isEmbed && <PrintButton />}
        </div>
      </header>
      <DashboardGrid items={dashboardData.items} />
    </section>
  )
}
